import "@fontsource/press-start-2p/latin-400.css";
import "@fontsource/pixelify-sans/latin-500.css";
import "@fontsource/pixelify-sans/latin-700.css";
import "@fontsource/dm-sans/latin-400.css";
import "@fontsource/dm-sans/latin-500.css";
import "@fontsource/dm-sans/latin-700.css";
import "./game-screen.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccountSheet } from "./AccountSheet";
import { BookView } from "./BookView";
import { TrophyCabinet } from "./TrophyCabinet";
import { DebugToolbar } from "./DebugToolbar";
import { FriendsSheet } from "./FriendsSheet";
import { HardwareButtons, type HardwareKey } from "./HardwareButtons";
import { ACTS } from "./icons";
import { Lcd } from "./Lcd";
import { MenuBar } from "./MenuBar";
import { MoreSheet } from "./MoreSheet";
import { RenameSheet } from "./RenameSheet";
import { RotateOverlay } from "./RotateOverlay";
import { SettingsSheet } from "./SettingsSheet";
import { Shell } from "./Shell";
import { ShopSheet } from "./ShopSheet";
import type { GameViewProps, MenuId, Screen } from "./view-model";

export function GameScreen(props: GameViewProps) {
  const {
    status,
    species,
    petName,
    meters,
    score,
    mood,
    sleeping,
    settings,
    reducedMotion,
    looks = { shellColour: 0, shellPattern: 0, background: 0, toy: 0, companion: 0, decoration: 0 },
    options,
    editing = false,
    friendBadge = 0,
    toasts,
    screen = "game",
    book = [],
    visiting = null,
    previous = null,
    onMenu = () => {},
    onCare,
    onHatch,
    onCloseBook = () => {},
    onVisit = () => {},
    onLeaveVisit = () => {},
    onPickLook = () => {},
    onSetting = () => {},
    onRename = () => {},
    onDismissToast,
    account,
    accountActions,
    friends,
    friendsActions,
    consent,
    consentActions,
    usageCounts,
    badges,
    dripWeeksLeft,
    shop,
    shopActions,
    debug,
  } = props;

  const [selectedAction, setSelectedAction] = useState(0);
  const [settingsSheet, setSettingsSheet] = useState<
    "settings" | "name" | "account" | "friends" | "badges" | "shop" | "more" | null
  >(() => {
    if (account?.status === "conflict" && screen !== "book") {
      return "account";
    }
    if (friends?.signedIn && friends.pendingCode && screen !== "book") {
      return "friends";
    }
    return null;
  });
  const settingsOpenerRef = useRef<HTMLElement | null>(null);
  const retro = settings.controls === "retro";

  // The shell slides away first (0.65 s, none with reduced motion), then the book opens.
  const [shellAway, setShellAway] = useState(false);
  useEffect(() => {
    if (screen !== "book") return;
    const timer = window.setTimeout(() => setShellAway(true), reducedMotion ? 0 : 650);
    return () => {
      window.clearTimeout(timer);
      setShellAway(false);
    };
  }, [screen, reducedMotion]);
  const bookOpen = screen === "book" && shellAway;

  // A friend's book opens with the same slide animation as the normal book.
  const isFriendBook = Boolean(friends?.book);
  const [friendShellAway, setFriendShellAway] = useState(false);
  useEffect(() => {
    if (!isFriendBook) return;
    const timer = window.setTimeout(() => setFriendShellAway(true), reducedMotion ? 0 : 650);
    return () => {
      window.clearTimeout(timer);
      setFriendShellAway(false);
    };
  }, [isFriendBook, reducedMotion]);
  const friendBookOpen = isFriendBook && friendShellAway;

  // The trophy cabinet slides away the shell (0.65 s, none with reduced motion) like the book.
  const isCabinet = settingsSheet === "badges";
  const [cabinetShellAway, setCabinetShellAway] = useState(false);
  useEffect(() => {
    if (!isCabinet) return;
    const timer = window.setTimeout(() => setCabinetShellAway(true), reducedMotion ? 0 : 650);
    return () => {
      window.clearTimeout(timer);
      setCabinetShellAway(false);
    };
  }, [isCabinet, reducedMotion]);
  const cabinetOpen = isCabinet && cabinetShellAway;

  const handleCloseFriendBook = useCallback(() => {
    setSettingsSheet("friends");
    friendsActions?.onCloseFriendBook();
  }, [friendsActions]);

  // The Monday sequence plays once; it is held here so a visit or a view refresh never replays it. The book stays shut meanwhile.
  const [sequenceDone, setSequenceDone] = useState(false);
  const finishSequence = useCallback(() => setSequenceDone(true), []);
  const sequencePlaying = status === "no-pet" && previous !== null && !sequenceDone;
  const hatchAndReset = useCallback(
    (name: string) => {
      setSequenceDone(false);
      onHatch(name);
    },
    [onHatch],
  );

  const conflictOpenedRef = useRef(false);
  useEffect(() => {
    if (account?.status === "conflict") {
      if (!conflictOpenedRef.current && screen !== "book" && !isCabinet && !isFriendBook && !sequencePlaying) {
        conflictOpenedRef.current = true;
        settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="menu-settings"]');
        setSettingsSheet("account");
      }
    } else {
      conflictOpenedRef.current = false;
    }
  }, [account?.status, isCabinet, isFriendBook, screen, sequencePlaying]);

  const lastPendingCodeRef = useRef<string | null>(null);
  useEffect(() => {
    if (friends?.signedIn && friends.pendingCode) {
      if (lastPendingCodeRef.current !== friends.pendingCode && screen !== "book" && !isCabinet && !isFriendBook && !sequencePlaying) {
        lastPendingCodeRef.current = friends.pendingCode;
        settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="menu-friends"]');
        setSettingsSheet("friends");
      }
    } else if (!friends?.pendingCode) {
      lastPendingCodeRef.current = null;
    }
  }, [friends?.signedIn, friends?.pendingCode, isCabinet, isFriendBook, screen, sequencePlaying]);

  const handleOpenSettings = useCallback(() => {
    if (screen === "visit") {
      onCloseBook();
    }
    if (editing) {
      onMenu("items");
    }
    settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="menu-settings"]');
    setSettingsSheet("settings");
  }, [editing, onCloseBook, onMenu, screen]);

  const handleOpenFriends = useCallback(() => {
    if (screen === "visit") {
      onCloseBook();
    }
    if (editing) {
      onMenu("items");
    }
    settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="menu-friends"]');
    setSettingsSheet("friends");
  }, [editing, onCloseBook, onMenu, screen]);

  const handleOpenShop = useCallback(() => {
    if (screen === "visit") {
      onCloseBook();
    }
    if (editing) {
      onMenu("items");
    }
    settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="menu-shop"]');
    setSettingsSheet("shop");
  }, [editing, onCloseBook, onMenu, screen]);

  const focusOpenerOrFallback = useCallback(() => {
    const opener = settingsOpenerRef.current;
    if (opener && opener.isConnected) {
      opener.focus();
    } else {
      const fallback = document.querySelector<HTMLElement>('[data-testid="menu-more"]');
      fallback?.focus();
    }
  }, []);

  const handleCloseSettings = useCallback(() => {
    setSettingsSheet(null);
    focusOpenerOrFallback();
  }, [focusOpenerOrFallback]);

  const handleOpenRename = useCallback(() => {
    settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="setting-rename"]');
    setSettingsSheet("name");
  }, []);

  const handleOpenAccount = useCallback(() => {
    settingsOpenerRef.current =
      document.querySelector<HTMLElement>('[data-testid="setting-account"]') ??
      document.querySelector<HTMLElement>('[data-testid="friends-signin"]') ??
      document.querySelector<HTMLElement>('[data-testid="menu-more"]');
    setSettingsSheet("account");
  }, []);

  const handleOpenBadges = useCallback(() => {
    if (screen === "visit") {
      onCloseBook();
    }
    if (editing) {
      onMenu("items");
    }
    settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="menu-trophies"]');
    setSettingsSheet("badges");
  }, [editing, onCloseBook, onMenu, screen]);

  const handleOpenMore = useCallback(() => {
    if (screen === "visit") {
      onCloseBook();
    }
    if (editing) {
      onMenu("items");
    }
    settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="menu-more"]');
    setSettingsSheet("more");
  }, [editing, onCloseBook, onMenu, screen]);

  const handleRename = useCallback(
    (name: string) => {
      onRename(name);
      setSettingsSheet(null);
      focusOpenerOrFallback();
    },
    [focusOpenerOrFallback, onRename],
  );

  const handleMenu = useCallback(
    (id: MenuId) => {
      if (id === "badges" || id === "trophies") {
        if (settingsSheet === "badges") {
          handleCloseSettings();
        } else {
          handleOpenBadges();
        }
        return;
      }
      if (id === "more") {
        if (settingsSheet === "more") {
          handleCloseSettings();
        } else {
          handleOpenMore();
        }
        return;
      }
      if (id === "save") {
        settingsOpenerRef.current = document.querySelector<HTMLElement>('[data-testid="menu-more"]');
        setSettingsSheet("account");
        return;
      }
      if (id === "shop") {
        if (settingsSheet === "shop") {
          handleCloseSettings();
        } else {
          handleOpenShop();
        }
        return;
      }
      if (id === "friends") {
        if (settingsSheet === "friends") {
          handleCloseSettings();
        } else {
          handleOpenFriends();
        }
        return;
      }
      if (id === "settings") {
        if (
          settingsSheet !== null &&
          settingsSheet !== "friends" &&
          settingsSheet !== "shop" &&
          settingsSheet !== "badges" &&
          settingsSheet !== "more"
        ) {
          handleCloseSettings();
        } else {
          handleOpenSettings();
        }
        return;
      }
      if (settingsSheet !== null) {
        setSettingsSheet(null);
      }
      onMenu(id);
    },
    [
      handleCloseSettings,
      handleOpenBadges,
      handleOpenFriends,
      handleOpenMore,
      handleOpenSettings,
      handleOpenShop,
      onMenu,
      settingsSheet,
    ],
  );

  const effectiveSettingsSheet =
    screen === "book" || sequencePlaying || isFriendBook ? null : settingsSheet;

  // Keep keyboard focus somewhere sensible when views swap: the visit bar's Back button, or the Book button after leaving.
  const lastScreen = useRef<Screen>(screen);
  useEffect(() => {
    const before = lastScreen.current;
    lastScreen.current = screen;
    if (before === screen) return;
    if (screen === "visit") document.querySelector<HTMLElement>('[data-testid="visit-back"]')?.focus();
    else if (screen === "game") document.querySelector<HTMLElement>('[data-testid="menu-book"]')?.focus();
  }, [screen]);

  const handleHardwarePress = useCallback(
    (key: HardwareKey) => {
      if (!retro || (status !== "alive" && screen !== "visit") || editing || effectiveSettingsSheet !== null) return;
      if (key === "A") {
        setSelectedAction((s) => (s + ACTS.length - 1) % ACTS.length);
      } else if (key === "C") {
        setSelectedAction((s) => (s + 1) % ACTS.length);
      } else {
        const act = ACTS[selectedAction] ?? ACTS[0]!;
        onCare(act.m);
      }
    },
    [editing, effectiveSettingsSheet, onCare, retro, screen, selectedAction, status],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        // A sheet or the cabinet handles its own Escape (inner view first, then closing), so this only acts when none is open.
        if (effectiveSettingsSheet !== null) {
          return;
        } else if (editing) {
          onMenu("items");
        } else if (screen === "book") {
          onCloseBook();
        } else if (friends?.book) {
          handleCloseFriendBook();
        }
        return;
      }
      if (!retro || effectiveSettingsSheet !== null || isCabinet || screen === "book" || isFriendBook) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "BUTTON")) {
        return;
      }
      if (e.key === "ArrowLeft") {
        handleHardwarePress("A");
      } else if (e.key === "ArrowRight") {
        handleHardwarePress("C");
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleHardwarePress("B");
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editing, effectiveSettingsSheet, friends?.book, handleCloseFriendBook, handleHardwarePress, isCabinet, isFriendBook, onCloseBook, onMenu, retro, screen]);

  const rootClasses = [
    "page",
    "game-screen",
    settings.largeText ? "game-screen-large-text" : "",
    reducedMotion ? "calm game-screen-reduced-motion" : "",
    retro ? "game-screen-retro" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const isOverlayOpen =
    effectiveSettingsSheet !== null &&
    effectiveSettingsSheet !== "badges";

  return (
    <main
      className={rootClasses}
      data-testid="game-screen"
      data-status={status}
      data-mood={mood}
      data-screen={screen}
    >
      <RotateOverlay />
      <div className="dev" inert={isOverlayOpen ? true : undefined}>
        <MenuBar
          friendBadge={friendBadge}
          bookOpen={screen !== "game"}
          bookDisabled={sequencePlaying || isFriendBook}
          trophiesOpen={effectiveSettingsSheet === "badges"}
          trophiesDisabled={sequencePlaying || screen === "book" || isFriendBook}
          editing={editing}
          itemsDisabled={sequencePlaying || screen !== "game" || isFriendBook}
          moreOpen={effectiveSettingsSheet === "more"}
          moreDisabled={sequencePlaying || screen === "book" || isFriendBook}
          onMenu={handleMenu}
        />

        {bookOpen ? <BookView book={book} onClose={onCloseBook} onVisit={onVisit} /> : null}
        {friends?.book && friendBookOpen ? (
          <BookView
            readOnly
            title={friends.book.title}
            book={friends.book.entries}
            onClose={handleCloseFriendBook}
          />
        ) : null}
        {cabinetOpen ? (
          <TrophyCabinet
            badges={badges}
            dripWeeksLeft={dripWeeksLeft}
            onClose={handleCloseSettings}
          />
        ) : null}

        <Shell
          looks={looks}
          retro={retro}
          away={screen === "book" || isFriendBook || isCabinet}
          hidden={bookOpen || friendBookOpen || cabinetOpen}
          hardwareButtons={<HardwareButtons retro={retro} onPress={handleHardwarePress} />}
        >
          <Lcd
            status={status}
            species={species}
            petName={petName}
            meters={meters}
            score={score}
            mood={mood}
            sleeping={sleeping}
            settings={settings}
            reducedMotion={reducedMotion}
            toasts={toasts}
            selectedAction={selectedAction}
            looks={looks}
            options={options}
            editing={editing}
            previous={previous}
            sequenceDone={sequenceDone}
            activeSettingsSheet={effectiveSettingsSheet}
            onSequenceDone={finishSequence}
            visiting={screen === "visit" ? visiting : null}
            onLeaveVisit={onLeaveVisit}
            onCare={onCare}
            onHatch={hatchAndReset}
            onDismissToast={onDismissToast}
            onPickLook={onPickLook}
            onMenu={handleMenu}
            badges={badges}
            dripWeeksLeft={dripWeeksLeft}
            onCloseSettingsSheet={handleCloseSettings}
          />
        </Shell>

        {debug ? <DebugToolbar debug={debug} /> : null}
      </div>

      {effectiveSettingsSheet === "more" ? (
        <MoreSheet
          friendBadge={friendBadge}
          account={account}
          onOpenFriends={() => handleMenu("friends")}
          onOpenShop={() => handleMenu("shop")}
          onOpenSettings={() => handleMenu("settings")}
          onOpenSave={() => handleMenu("save")}
          onClose={handleCloseSettings}
        />
      ) : null}

      {effectiveSettingsSheet === "settings" ? (
        <SettingsSheet
          settings={settings}
          reducedMotion={reducedMotion}
          status={status}
          petName={petName}
          account={account}
          badges={badges}
          usageCounts={usageCounts}
          onSetting={onSetting}
          onOpenRename={handleOpenRename}
          onOpenAccount={handleOpenAccount}
          onClose={handleCloseSettings}
        />
      ) : null}

      {effectiveSettingsSheet === "shop" ? (
        <ShopSheet
          shop={shop}
          shopActions={shopActions}
          onOpenAccount={handleOpenAccount}
          onClose={handleCloseSettings}
        />
      ) : null}

      {effectiveSettingsSheet === "friends" ? (
        <FriendsSheet
          friends={friends}
          actions={friendsActions}
          onOpenAccount={handleOpenAccount}
          onClose={handleCloseSettings}
        />
      ) : null}

      {effectiveSettingsSheet === "account" ? (
        <AccountSheet
          account={account}
          actions={accountActions}
          consent={consent}
          consentActions={consentActions}
          onClose={handleCloseSettings}
        />
      ) : null}

      {effectiveSettingsSheet === "name" ? (
        <RenameSheet
          currentName={petName}
          onRename={handleRename}
          onClose={handleCloseSettings}
        />
      ) : null}
    </main>
  );
}
